import {
  REMINDER_NODE_TEMPLATE_VARIABLES,
  type ReminderTemplateVariableKey
} from './reminder.constants'

/**
 * Builders that emit the HTML the email body's RichTextEditor (MULTI_LINE
 * variant) serialises, so a hand-authored template round-trips back into the
 * editor with its formatting and variable chips intact rather than as plain
 * text. Composing a template from these reads closer to the rendered email and
 * keeps further variants simple to author, e.g.
 *
 *   paragraph(`Hello, ${bold('there')} — ${templateVariable('contactName')}`)
 */

// A bare variable token keyed by backend key, e.g. `{{contactName}}` — the form
// the editor and backend resolve against. The MULTI_LINE body wraps this in a
// mention span (see templateVariable); the plain-text subject/To fields use it
// directly.
const variableToken = (key: ReminderTemplateVariableKey): string => `{{${key}}}`

export const paragraph = (content: string): string => `${content}<br/>`

export const bold = (content: string): string => `<b>${content}</b>`

// No rel/target: the editor stores a bare anchor and EmailPreview opens links in
// a new window when it renders them, matching the RichTextEditor's output.
export const link = (text: string, href: string): string =>
  `<a href="${href}">${text}</a>`

// Inline variables render as Tiptap mention atoms, mirroring how the editor
// serialises a mention (see MentionView.tsx / RichTextEditor's mention
// renderHTML): the backend key drives `data-id` and the `{{key}}` token, the
// human label is carried in `data-label` for display, and
// `data-mention-suggestion-char` is the `{` that triggers the variable menu.
export const templateVariable = (key: ReminderTemplateVariableKey): string => {
  const label = REMINDER_NODE_TEMPLATE_VARIABLES[key]

  return `<span data-type="mention" data-id="${key}" data-label="${label}" data-mention-suggestion-char="{">${variableToken(key)}</span>`
}

// Seed the To field with the contact email variable so a new reminder is
// addressed to the invoice's contact out of the box.
export const DEFAULT_REMINDER_RECIPIENTS: string[] = [
  variableToken('contactEmail')
]

// The subject is a single-line, plain-text field (TEXT output), so the variable
// is the bare token rather than a mention span like the body uses.
export const DEFAULT_REMINDER_SUBJECT: string = `Invoice overdue: Invoice ${variableToken(
  'invoiceNumber'
)}`

// A bare paragraph('') emits an empty <br/> line, separating the blocks with a
// blank line the way the RichTextEditor serialises a deliberate gap.
export const DEFAULT_REMINDER_TEMPLATE: string =
  paragraph('Hi team,') +
  paragraph('') +
  paragraph(
    `Friendly reminder that invoice ${templateVariable(
      'invoiceNumber'
    )} for ${templateVariable('amount')}, which was due on ${templateVariable(
      'dueDate'
    )} is now ${templateVariable('daysLate')} days past due.`
  ) +
  paragraph('') +
  paragraph(
    `You can view and pay the invoice here: ${link(
      templateVariable('url'),
      variableToken('url')
    )}`
  ) +
  paragraph('') +
  paragraph('Thank you!')
